import { useDispatch, useSelector } from "react-redux";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "src/components/ui/select.component";
import { invoiceActions } from "src/context/invoice-slice";
import { getAnalyticsData, getAssigneeList } from "src/services/invoice-api";
import { Fragment, useCallback, useEffect, useState } from "react";
import { Button, Input, } from "src/components";
import InvoiceApprovalContainer from "./InvoiceApprovalContainer";
import { Tab, Transition } from "@headlessui/react";
import { RootState } from "src/context/store";
import InvoiceApprovalFiltersComponent from "src/features/fields/container/InvoiceApprovalFilters";
import { useOutsideClick } from "src/hooks/useOutsideClick";
import filterIcon from "src/assets/filterIcon.svg";
import classNames from "classnames";
import InvoiceApprovalPriorityTab from "./invoiceApprovalPriorityTab";
import InvoiceApprovalEscalatedTab from "./invoiceApprovalEscalatedTab";
import { queryDivisionDetails } from "src/services/division-api";
import { searchOptions } from "./invoice-helper";

const InvoiceApproval = () => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [searchCategory, setSearchCategory] = useState("invoice_number");
  const [searchValue, setSearchValue] = useState("");
  const { InvoiceApprovalFilters, InvoiceFiltersData, invoiceAnalysisData } = useSelector((state: RootState) => state.invoice);

  const [selectedTab, setSelectedTab] = useState(0);
  const dispatch = useDispatch();
  const ref = useOutsideClick(() => setIsFilterOpen(false));

  const company_code = InvoiceApprovalFilters?.company?.[0]?.code;

  // Fetch divisions when company changes
  useEffect(() => {
    if (InvoiceApprovalFilters?.company?.length) {
      queryDivisionDetails(`/details/?fields=division_name&company_code=${InvoiceApprovalFilters?.company?.[0]?.code}`)
        .then((res) => dispatch(invoiceActions.setInvoiceFiltersData({ key: "division", value: res?.data })))
        .catch((err) => console.error(err));
    } else {
      dispatch(invoiceActions.setInvoiceFiltersData({ key: "division", value: [] }));
    }
    if (InvoiceApprovalFilters?.company?.find(company => company.code === 618)) {
      dispatch(invoiceActions.setInvoiceFilters({ key: "assignee", value: [] }));
      getAssigneeList(company_code)
        .then((res) => dispatch(invoiceActions.setInvoiceFiltersData({ key: "assignee", value: res?.data?.data })))
        .catch((err) => console.error(err))
    } else {
      getAssigneeList(company_code)
        .then((res) => dispatch(invoiceActions.setInvoiceFiltersData({ key: "assignee", value: res?.data?.data })))
        .catch((err) => console.error(err))
    }
  }, [dispatch, InvoiceApprovalFilters?.company]);

  //Fetch counts of all invoices
  const fetchDataCount = useCallback(async () => {
    const analyticsData = await getAnalyticsData(company_code)
    dispatch(invoiceActions.setInvoiceAnalysisData(
      {
        entry: {
          "All Invoices": analyticsData?.data?.data?.yet_to_processed_count,
          "Priority Tasks": analyticsData?.data?.data?.yet_to_processed_prioritised,
          "Escalated": analyticsData?.data?.data?.yet_to_processed_escalated,
          "Rejection Review": 0,
          "Feedback from Ext": 0,
        },
        approval: {
          "All Invoices": analyticsData?.data?.data?.waiting_for_approval_count,
          "Priority Tasks": analyticsData?.data?.data?.waiting_for_approval_prioritised,
          "Escalated": analyticsData?.data?.data?.waiting_for_approval_escalated,
        }
      }
    ));
  }, []);

  useEffect(() => {
    if (Object.values(invoiceAnalysisData?.approval).every((value) => value === 0)) {
      fetchDataCount()
    }
  }, []);

  return (
    <div className="relative">
      <Transition
        as={Fragment}
        show={isFilterOpen}
        enter="transition ease-in-out duration-200 transform"
        enterFrom="translate-x-full"
        enterTo="translate-x-0"
        leave="transition ease-in-out duration-200 transform"
        leaveFrom="translate-x-0"
        leaveTo="translate-x-full"
      >
        <div className="fixed right-0 inset-0 top-0 z-[500]">
          <InvoiceApprovalFiltersComponent
            companyDetails={InvoiceFiltersData?.company || []}
            divisionDetails={InvoiceFiltersData?.division || []}
            stockistDetails={InvoiceFiltersData?.stockist || []}
            statusDetails={InvoiceFiltersData?.status || []}
            assigneeDetails={InvoiceFiltersData?.assignee || []}
            cityDetails={InvoiceFiltersData?.city || []}
            districtDetails={InvoiceFiltersData?.district || []}
            stateDetails={InvoiceFiltersData?.state || []}
            ref={ref}
            onClose={() => setIsFilterOpen(false)}
          />
        </div>
      </Transition>
      <div className="mx-auto">
        <div className="px-2 py-5 w-full sm:px-0">
          <Tab.Group selectedIndex={selectedTab} onChange={setSelectedTab}>
            <div className="flex justify-between">
              <Tab.List className="flex p-1 space-x-1 rounded-lg bg-[#e3e3e3] w-[500px] h-[38px] items-center">
                {Object.entries(invoiceAnalysisData?.approval).map(([name, count], index) => (
                  <Tab
                    key={index}
                    className={({ selected }) =>
                      classNames(
                        "w-full rounded-md py-1.5 text-sm font-normal leading-5",
                        "focus:outline-none",
                        selected ? "bg-white" : "hover:bg-white/[0.12] hover:text-black"
                      )
                    }
                  >
                    {name}
                    {count !== null && (
                      <span className="ml-4 bg-[#8051FF] text-white text-[12px] px-2 py-1 rounded-lg">{count}</span>
                    )}
                  </Tab>
                ))}
              </Tab.List>

              <div className="flex items-center gap-2 h-full">
                <div className="flex items-center w-[400px] rounded-md overflow-hidden border border-gray-300 bg-white">
                  <Select value={searchCategory} onValueChange={setSearchCategory}>
                    <SelectTrigger className="w-[160px] rounded-none border-none bg-white text-gray-700 focus:ring-0 focus:outline-none focus:shadow-none">
                      <SelectValue placeholder="Search By" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {searchOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder="Search"
                    debounceTime={1250} //M10-11601
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value.trim())}
                    containerClasses="w-full px-3 py-1 text-gray-700 border-none bg-white focus:outline-none focus:ring-0 focus:shadow-none"
                  />
                </div>
                <div className="flex items-center gap-2 h-full relative">
                  {Object.values(InvoiceApprovalFilters).some((value: any) => value?.length > 0) && (
                    <span className="absolute top-[-10px] right-[-10px] w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white text-xs">
                      {Object.values(InvoiceApprovalFilters).filter((value: any) => value?.length > 0).length}
                    </span>
                  )}
                  <Button className="bg-[#F1F1F1]" onClick={() => setIsFilterOpen(true)}>
                    <img src={filterIcon} alt="filter-icon" />
                  </Button>
                </div>
              </div>
            </div>
            <Tab.Panels className="">
              <Tab.Panel>
                <InvoiceApprovalContainer searchCategory={searchCategory} searchValue={searchValue} />
              </Tab.Panel>
              <Tab.Panel>
                <InvoiceApprovalPriorityTab searchCategory={searchCategory} searchValue={searchValue} />
              </Tab.Panel>
              <Tab.Panel>
                <InvoiceApprovalEscalatedTab searchCategory={searchCategory} searchValue={searchValue} />
              </Tab.Panel>
            </Tab.Panels>
          </Tab.Group>
        </div>
      </div>
    </div>
  );
};

export default InvoiceApproval;
